import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface ResourcesBarProps {
  money: number
  actionPoints: number
  maxActionPoints?: number
  prestige: number
  momentum: number
  className?: string
}

const RESOURCE_INFO = {
  money: {
    icon: '💰',
    label: 'Money',
    tooltip: 'Currency for campaigns, recruitment, and actions',
  },
  actionPoints: {
    icon: '⚡',
    label: 'Action Points',
    tooltip: 'Spent each tick on party actions. Regenerates daily.',
  },
  prestige: {
    icon: '📊',
    label: 'Prestige',
    tooltip: 'Long-term reputation (0-100). Affects voter appeal.',
  },
  momentum: {
    icon: '📈',
    label: 'Momentum',
    tooltip: 'Short-term trend (-50 to +50). Affects polls and elections.',
  },
}

export function ResourcesBar({
  money,
  actionPoints,
  maxActionPoints = 10,
  prestige,
  momentum,
  className,
}: ResourcesBarProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg md:grid-cols-4',
        className
      )}
    >
      <ResourceItem
        icon={RESOURCE_INFO.money.icon}
        label={RESOURCE_INFO.money.label}
        tooltip={RESOURCE_INFO.money.tooltip}
        value={formatMoney(money)}
      />

      <ResourceItem
        icon={RESOURCE_INFO.actionPoints.icon}
        label={RESOURCE_INFO.actionPoints.label}
        tooltip={RESOURCE_INFO.actionPoints.tooltip}
        value={`${actionPoints} / ${maxActionPoints}`}
        progress={(actionPoints / maxActionPoints) * 100}
        progressColor="bg-amber-500"
      />

      <ResourceItem
        icon={RESOURCE_INFO.prestige.icon}
        label={RESOURCE_INFO.prestige.label}
        tooltip={RESOURCE_INFO.prestige.tooltip}
        value={`${prestige}`}
        progress={prestige}
        progressColor="bg-purple-500"
      />

      <MomentumItem
        icon={RESOURCE_INFO.momentum.icon}
        label={RESOURCE_INFO.momentum.label}
        tooltip={RESOURCE_INFO.momentum.tooltip}
        value={momentum}
      />
    </div>
  )
}

interface ResourceItemProps {
  icon: string
  label: string
  tooltip: string
  value: string
  progress?: number
  progressColor?: string
}

function ResourceItem({
  icon,
  label,
  tooltip,
  value,
  progress,
  progressColor = 'bg-primary',
}: ResourceItemProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex flex-col gap-1 cursor-help">
          <div className="flex items-center gap-2">
            <span className="text-lg">{icon}</span>
            <span className="text-sm font-medium">{value}</span>
          </div>
          <span className="text-xs text-muted-foreground">{label}</span>
          {progress !== undefined && (
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={cn('h-full transition-all rounded-full', progressColor)}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}

interface MomentumItemProps {
  icon: string
  label: string
  tooltip: string
  value: number
}

function MomentumItem({ icon, label, tooltip, value }: MomentumItemProps) {
  const isPositive = value > 0
  const isNegative = value < 0
  const displayIcon = isNegative ? '📉' : icon

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex flex-col gap-1 cursor-help">
          <div className="flex items-center gap-2">
            <span className="text-lg">{displayIcon}</span>
            <span
              className={cn(
                'text-sm font-medium tabular-nums',
                isPositive && 'text-green-600',
                isNegative && 'text-red-600'
              )}
            >
              {isPositive ? '+' : ''}
              {value}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">{label}</span>
          <div className="flex items-center gap-1">
            {isPositive && (
              <span className="text-xs text-green-600">▲ Trending up</span>
            )}
            {isNegative && (
              <span className="text-xs text-red-600">▼ Trending down</span>
            )}
            {!isPositive && !isNegative && (
              <span className="text-xs text-muted-foreground">— Stable</span>
            )}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}

function formatMoney(amount: number): string {
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1)}M`
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(1)}K`
  }
  return amount.toLocaleString()
}
